import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { RiArrowLeftLine, RiRefreshLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { SearchField } from '@/design-system/search-field';
import { Switch } from '@/design-system/switch';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { ujpAPI, type UjpClientConfig } from '@/services/api/ujp';

import MoneyInput from './components/MoneyInput';
import { UJP_COPY } from './copy';
import { formatDateTime, formatRupiah, toNum } from './format';

/**
 * `/ujp/config` — the per-client UJP rule (CR-D7): are the pool legs (leaving
 * the pool and returning to it) charged and what does a reverse trip add.
 * (E-money now defaults from the vehicle master, not the client rule.)
 *
 * Both halves of the rule change money: `chargedPositioning` decides the km the
 * client is billed for AND which stops end up on the shipment created at
 * approval. R3: editable by any signed-in back-office user (ops) — the server
 * now allows any ADMIN on PUT (tariff + margin stay approver-only). This is the
 * affordance, not the boundary.
 */

interface ConfigRow {
  clientId: number;
  clientName: string;
  chargedPositioning: boolean;
  reverseCharge: string;
  notes: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  /** No `ujp_client_configs` row yet — the defaults are implied. */
  unset: boolean;
}

interface DraftRow {
  chargedPositioning: boolean;
  /** Digits string for MoneyInput. */
  reverseCharge: string;
  notes: string;
}

const UjpConfigPage: React.FC = () => {
  const c = UJP_COPY.config;
  const { user } = useAuth();
  // /config (client commercial switches) is ops-editable (any signed-in
  // back-office user) — mirrors the BE. Tariff + margin stay approver-only.
  const canEdit = !!user?.email;

  const clients = useProvidersDropdown(true);
  const [configs, setConfigs] = useState<UjpClientConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DraftRow | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setConfigs(await ujpAPI.listClientConfigs());
    } catch (err) {
      setConfigs([]);
      setError(err instanceof Error ? err.message : c.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [c.loadFailed]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Every visible client is a row; an absent config means the defaults
  // (Client Configs - List: "Absent client = charged false, reverse 0").
  const rows = useMemo<ConfigRow[]>(() => {
    const byClient = new Map(configs.map((cfg) => [Number(cfg.clientId), cfg]));
    return clients.options.map((p) => {
      const cfg = byClient.get(Number(p.id));
      return {
        clientId: Number(p.id),
        clientName: p.name,
        chargedPositioning: cfg?.chargedPositioning ?? false,
        reverseCharge: cfg?.reverseCharge ?? '0',
        notes: cfg?.notes ?? null,
        updatedBy: cfg?.updatedBy ?? null,
        updatedAt: cfg?.updatedAt ?? null,
        unset: !cfg,
      };
    });
  }, [clients.options, configs]);

  const startEdit = (row: ConfigRow) => {
    setEditingId(row.clientId);
    setDraft({
      chargedPositioning: row.chargedPositioning,
      reverseCharge: String(Math.round(toNum(row.reverseCharge))),
      notes: row.notes ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const save = async (row: ConfigRow) => {
    if (!draft) return;
    setSaving(true);
    try {
      const updated = await ujpAPI.putClientConfig(row.clientId, {
        chargedPositioning: draft.chargedPositioning,
        reverseCharge: draft.reverseCharge || '0',
        notes: draft.notes.trim() || null,
      });
      setConfigs((prev) => {
        const rest = prev.filter(
          (x) => Number(x.clientId) !== Number(row.clientId)
        );
        return [...rest, updated];
      });
      toast.success(c.saved(row.clientName));
      cancelEdit();
    } catch (err) {
      toast.error(c.saveFailed, {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const columns: TableDataColumn<ConfigRow>[] = [
    {
      key: 'clientName',
      title: c.columns.client,
      render: (_v, r) => (
        <span className="text-[13px] tracking-tight text-text-strong-950">
          {r.clientName}
        </span>
      ),
    },
    {
      key: 'chargedPositioning',
      title: c.columns.charged,
      render: (_v, r) =>
        editingId === r.clientId && draft ? (
          <Switch
            aria-label={`${c.columns.charged} — ${r.clientName}`}
            checked={draft.chargedPositioning}
            disabled={saving}
            onCheckedChange={(v) =>
              setDraft((d) => (d ? { ...d, chargedPositioning: v } : d))
            }
          />
        ) : (
          <Badge status={r.chargedPositioning ? 'success' : 'neutral'} size="sm">
            {r.chargedPositioning ? 'Ya' : 'Tidak'}
          </Badge>
        ),
    },
    {
      key: 'reverseCharge',
      title: c.columns.reverse,
      align: 'right',
      render: (_v, r) =>
        editingId === r.clientId && draft ? (
          <div className="w-40">
            <MoneyInput
              aria-label={`${c.columns.reverse} — ${r.clientName}`}
              value={draft.reverseCharge}
              size="sm"
              disabled={saving}
              onChange={(v) => setDraft((d) => (d ? { ...d, reverseCharge: v } : d))}
            />
          </div>
        ) : (
          <span className="font-mono text-[12px] tabular-nums text-text-strong-950">
            {formatRupiah(r.reverseCharge)}
          </span>
        ),
    },
    {
      key: 'notes',
      title: c.columns.notes,
      render: (_v, r) =>
        editingId === r.clientId && draft ? (
          <InputRoot size="sm">
            <Input
              className="min-w-0 w-full"
              aria-label={`${c.columns.notes} — ${r.clientName}`}
              value={draft.notes}
              placeholder={c.notesPlaceholder}
              disabled={saving}
              onChange={(e) =>
                setDraft((d) => (d ? { ...d, notes: e.target.value } : d))
              }
            />
          </InputRoot>
        ) : (
          <span className="text-[12px] text-text-sub-600">{r.notes || '—'}</span>
        ),
    },
    {
      key: 'updatedAt',
      title: c.columns.updated,
      render: (_v, r) =>
        r.unset ? (
          <Badge status="neutral" size="sm">
            {c.unset}
          </Badge>
        ) : (
          <span className="text-[11px] text-text-sub-600">
            {formatDateTime(r.updatedAt)}
            {r.updatedBy ? ` · ${r.updatedBy.split('@')[0]}` : ''}
          </span>
        ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, r) => {
        if (!canEdit) return null;
        if (editingId === r.clientId) {
          return (
            <div className="inline-flex items-center justify-end gap-1">
              <Button
                tone="neutral"
                style="ghost"
                size="xs"
                onClick={cancelEdit}
                disabled={saving}
              >
                {UJP_COPY.wizard.cancel}
              </Button>
              <Button size="xs" onClick={() => save(r)} loading={saving}>
                {saving ? c.saving : c.save}
              </Button>
            </div>
          );
        }
        return (
          <Button
            tone="neutral"
            style="stroke"
            size="xs"
            onClick={() => startEdit(r)}
            disabled={editingId != null}
          >
            {UJP_COPY.wizard.edit}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">{c.eyebrow}</p>
          <h1 className="text-[28px] font-extrabold leading-none tracking-tight text-text-strong-950">
            {c.title}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-sub-600">
            {c.subtitle}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button tone="neutral" style="stroke" onClick={fetchConfigs}>
            <RiRefreshLine />
            {UJP_COPY.page.refresh}
          </Button>
          <Button tone="neutral" style="ghost" asChild>
            <Link to="/ujp">
              <RiArrowLeftLine />
              {c.back}
            </Link>
          </Button>
        </div>
      </div>

      {!canEdit && <Hint>{c.readOnly}</Hint>}

      <Card padding="none">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stroke-soft-200 px-4 py-3">
          <SearchField
            onChange={clients.setSearch}
            placeholder="Cari client…"
            size="sm"
            className="min-w-[220px] max-w-xs flex-1"
          />
          {clients.hasMore && (
            <Button
              tone="neutral"
              style="ghost"
              size="xs"
              onClick={clients.loadMore}
              loading={clients.loadingMore}
            >
              Muat lebih banyak
            </Button>
          )}
        </div>

        <div className="p-4">
          <TableData<ConfigRow>
            data={error ? [] : rows}
            columns={columns}
            loading={loading || clients.loading}
            size="sm"
            emptyState={
              error || clients.error ? (
                <EmptyState size="sm">
                  <EmptyStateTitle>{c.loadFailed}</EmptyStateTitle>
                  <EmptyStateDescription>
                    {error ?? clients.error}
                  </EmptyStateDescription>
                  <EmptyStateActions>
                    <Button
                      tone="neutral"
                      style="ghost"
                      size="sm"
                      onClick={fetchConfigs}
                    >
                      {UJP_COPY.page.retry}
                    </Button>
                  </EmptyStateActions>
                </EmptyState>
              ) : (
                <EmptyState size="sm">
                  <EmptyStateTitle>{c.empty}</EmptyStateTitle>
                </EmptyState>
              )
            }
          />
        </div>
      </Card>
    </div>
  );
};

export default UjpConfigPage;
